import React, { useEffect, useState, useCallback } from "react";
import { useEngagementAssessment } from "../../../hooks/useEngagementAssessment";
import { AIModalState } from "../../../enums/CompassAI";
import { HeroKind } from "../../../enums/Modal";
import { MESSAGES } from "../../../messages";
import { BatchAssessmentResult } from "../../../types/compassAITypes";

import {
  CompassModal,
  ModalContent,
  ModalFooter,
  useCompassModal,
  defineModalConfig,
} from "../Modal";
import { CompassParagraph } from "../styledComponents";
import { AssessmentResultsSummary } from "./AssessmentResultsSummary";
import {
  CompassWhiteButton,
  CompassGreenButton,
} from "../../buttons/styledComponents";
import { JobProgressBar } from "./JobProgressBar";

import { darkGreen, errorRed } from "../../../styles";

import aiIcon from "../../../assets/svg/compass_ai_modal_icon.svg";
import engagementRatingBg from "../../../assets/images/engagement_rating.png";
import aiJobRunningAnimation from "../../../assets/svg/ai_job_running_animation.svg";

// =============================================================================
// CONFIG
// =============================================================================

const messages = MESSAGES.COMPASS_AI.ENGAGEMENT_ASSESSMENT;

type ModalContext = { dotCounter: number };

const ASSESSMENT_MODAL_CONFIG = defineModalConfig<AIModalState, ModalContext>({
  title: messages.MODAL_TITLE,
  titleIcon: aiIcon,
  titleIconSize: 40,
  modalSize: "md",
  size: "lg",
  states: {
    [AIModalState.IDLE]: {
      hero: {
        kind: HeroKind.IMAGE,
        source: engagementRatingBg,
        padding: 0,
        imageBorderRadius: 8,
        backgroundColor: "#fafafa",
        title: messages.TITLE.IDLE,
        description: messages.DESCRIPTION.IDLE,
      },
    },
    [AIModalState.ANALYZING]: {
      isProcessing: true,
      hero: {
        kind: HeroKind.IMAGE,
        source: aiJobRunningAnimation,
        padding: 0,
        height: 280,
        backgroundColor: "#fafafa",
        title: messages.TITLE.ANALYZING,
        description: messages.DESCRIPTION.ANALYZING,
        suffix: (ctx) =>
          [1, 2, 3].map((i) => (
            <CompassParagraph
              key={i}
              color={ctx.dotCounter >= i ? darkGreen : "transparent"}
              mr={-2}
              fontSize={20}
              bold
            >
              .
            </CompassParagraph>
          )),
      },
    },
    [AIModalState.POLLING]: {
      isProcessing: true,
      hero: {
        kind: HeroKind.IMAGE,
        source: aiJobRunningAnimation,
        padding: 0,
        height: 280,
        backgroundColor: "#fafafa",
        title: messages.TITLE.POLLING,
        description: messages.DESCRIPTION.POLLING,
      },
    },
    [AIModalState.SUCCESS]: {},
    [AIModalState.ERROR]: {
      hero: {
        kind: HeroKind.IMAGE,
        source: engagementRatingBg,
        padding: 0,
        imageBorderRadius: 8,
        backgroundColor: "#fafafa",
        title: messages.TITLE.ERROR,
        titleColor: errorRed,
        accentColor: errorRed,
        description: messages.DESCRIPTION.ERROR,
      },
    },
  },
});

// =============================================================================
// COMPONENT
// =============================================================================

export interface EngagementAssessmentModalProps {
  show: boolean;
  onHide: () => void;
  onSuccess?: (result: BatchAssessmentResult) => void;
}

export const EngagementAssessmentModal: React.FC<
  EngagementAssessmentModalProps
> = ({ show, onHide, onSuccess }) => {
  const [dotCounter, setDotCounter] = useState(0);

  const {
    state,
    startAssessment,
    downloadFile,
    assessmentResult,
    progress,
    reset,
  } = useEngagementAssessment();

  useEffect(() => {
    if (state === AIModalState.ANALYZING) {
      const interval = setInterval(() => {
        setDotCounter((prev) => (prev + 1) % 4);
      }, 500);
      return () => clearInterval(interval);
    }
  }, [state]);

  const handleStartAssessment = async (): Promise<void> => {
    // No filters — assess all engagements for the tenant
    await startAssessment({});
  };

  useEffect(() => {
    if (state === AIModalState.SUCCESS && assessmentResult && onSuccess) {
      onSuccess(assessmentResult);
    }
  }, [state, assessmentResult]);

  const handleClose = useCallback(() => {
    reset();
    setDotCounter(0);
    onHide();
  }, [reset, onHide]);

  const handleDownloadHeader = useCallback(async () => {
    if (assessmentResult?.output_files?.assessments) {
      const url = await downloadFile(assessmentResult.output_files.assessments);
      if (url) window.open(url, "_blank");
    }
  }, [assessmentResult, downloadFile]);

  const handleDownloadDetails = useCallback(async () => {
    if (assessmentResult?.output_files?.assessment_details) {
      const url = await downloadFile(
        assessmentResult.output_files.assessment_details,
      );
      if (url) window.open(url, "_blank");
    }
  }, [assessmentResult, downloadFile]);

  const { modalProps, isProcessing } = useCompassModal({
    config: ASSESSMENT_MODAL_CONFIG,
    state,
    context: { dotCounter },
  });

  const renderFooter = (): React.ReactNode => {
    if (state === AIModalState.SUCCESS) {
      return (
        <ModalFooter showDivider={false}>
          {assessmentResult?.output_files?.assessments && (
            <CompassGreenButton onClick={handleDownloadHeader}>
              Download Assessments
            </CompassGreenButton>
          )}
          {assessmentResult?.output_files?.assessment_details && (
            <CompassWhiteButton onClick={handleDownloadDetails}>
              Download Details
            </CompassWhiteButton>
          )}
          <CompassWhiteButton onClick={handleClose}>Done</CompassWhiteButton>
        </ModalFooter>
      );
    }

    if (state === AIModalState.IDLE) {
      return (
        <ModalFooter
          onClose={handleClose}
          closeText="Cancel"
          submitText={messages.START_ASSESSMENT}
          onSubmit={handleStartAssessment}
          showDivider={false}
        />
      );
    }

    if (state === AIModalState.ERROR) {
      return (
        <ModalFooter
          onClose={handleClose}
          closeText="Close"
          submitText="Try Again"
          onSubmit={reset}
          showDivider={false}
        />
      );
    }

    return (
      <ModalFooter
        onClose={handleClose}
        closeText="Cancel"
        hideSubmit
        isLoading={isProcessing}
        showDivider={false}
      />
    );
  };

  return (
    <CompassModal
      show={show}
      onHide={handleClose}
      {...modalProps}
      footer={renderFooter()}
    >
      <ModalContent>
        {state === AIModalState.POLLING && progress && (
          <JobProgressBar progress={progress} />
        )}

        {state === AIModalState.SUCCESS && assessmentResult && (
          <AssessmentResultsSummary result={assessmentResult} />
        )}
      </ModalContent>
    </CompassModal>
  );
};
